import React from 'react';
import AuthImage from '../AuthImage';
import Spinner from '../Spinner';
import {AlertTriangleIcon, PlusIcon, RefreshIcon, SparklesIcon} from '../icons';
import {pluralItemsOf} from '../../utils/plural';

/**
 * Панель подсказок конструктора (UX-аудит F01/F08).
 *
 * Отвечает на вопрос текущего шага, а не «с чем носить последнюю выбранную вещь»:
 * кандидаты приходят из `POST /api/compatibility/for-set` уже отфильтрованными по слоту и
 * оценёнными относительно всего комплекта. Нажатие строки ставит вещь в слот
 * («Добавить»/«Заменить»), «Подробнее» — отдельное действие, черновик при нём не теряется.
 */

export interface CompatWarning {
    itemId: string;
    itemName: string;
    code: string;
    message: string;
}

export interface CompatCandidate {
    id: string;
    name: string;
    brandName: string | null;
    categoryId?: string | null;
    categoryName: string | null;
    primaryPhotoUrl: string | null;
    score: number;
    matchReasons: string[];
    unscored?: boolean;
    /** Средний балл по всему комплекту [0,1] (for-set). */
    setScore?: number | null;
    /** Сколько пар комплекта удалось оценить. */
    setScoredPairs?: number | null;
    /** Что улучшится — причины лучшей пары с комплектом. */
    setImproveReasons?: string[] | null;
    /** С какой вещью комплекта возможен конфликт. */
    setConflictWith?: string | null;
    /** Ограничения пригодности самого кандидата. */
    warnings?: CompatWarning[] | null;
}

interface Props {
    candidates: CompatCandidate[];
    loading: boolean;
    error: string | null;
    onRetry: () => void;
    /** Сколько вещей уже в комплекте — подпись «по N вещам». */
    setSize: number;
    /** Название текущего шага; `null` — итоговый экран. */
    stepLabel: string | null;
    /** Слот уже занят: основное действие — «Заменить». */
    slotFilled: boolean;
    onPick: (candidate: CompatCandidate) => void;
    onDetails: (candidate: CompatCandidate) => void;
}

const MAX_VISIBLE = 14;
const MAX_REASONS = 2;

const EMPTY_HINT = 'Для этого шага совместимых вещей не найдено.';

const BuilderCompatPanel: React.FC<Props> = ({
    candidates, loading, error, onRetry, setSize, stepLabel, slotFilled, onPick, onDetails,
}) => {
    const actionLabel = slotFilled ? 'Заменить' : 'Добавить';

    if (setSize === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-40 text-center text-gray-400 dark:text-gray-500 text-sm px-3">
                <SparklesIcon className="w-7 h-7 mb-2 opacity-40"/>
                Выберите вещь — покажем, что подходит к комплекту
            </div>
        );
    }

    const renderBody = () => {
        if (loading) {
            return <div className="flex justify-center py-4"><Spinner/></div>;
        }
        if (error) {
            return (
                <div className="space-y-2 px-1 py-2" data-testid="builder-compat-error">
                    <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
                    <button
                        type="button"
                        onClick={onRetry}
                        className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-xl border
                                   border-gray-300 dark:border-ink-700 text-xs text-gray-700 dark:text-gray-200
                                   hover:bg-gray-100 dark:hover:bg-ink-800 transition-colors"
                    >
                        <RefreshIcon className="w-3.5 h-3.5"/>
                        Повторить
                    </button>
                </div>
            );
        }
        if (candidates.length === 0) {
            return (
                <p className="text-xs text-gray-400 px-1 py-2" data-testid="builder-compat-empty-step">
                    {EMPTY_HINT}
                </p>
            );
        }
        return candidates.slice(0, MAX_VISIBLE).map(candidate => renderRow(candidate));
    };

    const renderRow = (candidate: CompatCandidate) => {
        const reasons = (candidate.setImproveReasons ?? candidate.matchReasons ?? []).slice(0, MAX_REASONS);
        const warnings = candidate.warnings ?? [];
        return (
            <div key={candidate.id} className="rounded-lg hover:bg-gray-50 dark:hover:bg-ink-800 transition-colors">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="builder-compat-item"
                        onClick={() => onPick(candidate)}
                        aria-label={`${actionLabel}: ${candidate.name}`}
                        className="flex items-center gap-2.5 flex-1 min-w-0 text-left px-2 py-1.5 min-h-[44px] rounded-lg"
                    >
                        <span className="w-9 h-9 rounded-lg overflow-hidden bg-gray-100 dark:bg-ink-700 flex-shrink-0 block">
                            {candidate.primaryPhotoUrl ? (
                                <AuthImage imageKey={candidate.primaryPhotoUrl} alt={candidate.name}
                                           variant="thumbnail" className="w-full h-full object-cover"/>
                            ) : (
                                <span className="w-full h-full flex items-center justify-center">
                                    <SparklesIcon className="w-4 h-4 text-gray-300"/>
                                </span>
                            )}
                        </span>
                        <span className="min-w-0 flex-1 block">
                            <span className="block text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                {candidate.name}
                            </span>
                            <span className="block text-xs text-gray-400 truncate">
                                {candidate.categoryName ? `${candidate.categoryName} · ` : ''}
                                подходит на {candidate.score} из 100
                            </span>
                        </span>
                        <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-lg px-2 py-1
                                         text-xs font-medium text-brand-600 dark:text-brand-400">
                            <PlusIcon className="w-3 h-3"/>
                            {actionLabel}
                        </span>
                    </button>
                    <button
                        type="button"
                        data-testid="builder-compat-details"
                        onClick={() => onDetails(candidate)}
                        aria-label={`Подробнее о вещи «${candidate.name}»`}
                        className="flex-shrink-0 min-h-[44px] px-2 text-xs text-gray-500 dark:text-gray-400
                                   hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                    >
                        Подробнее
                    </button>
                </div>
                {(reasons.length > 0 || candidate.setConflictWith || warnings.length > 0) && (
                    <div className="px-2 pb-1.5 space-y-1">
                        {reasons.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {reasons.map(reason => (
                                    <span key={reason}
                                          className="inline-flex items-center rounded-full bg-gray-100 dark:bg-ink-800
                                                     px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300">
                                        {reason}
                                    </span>
                                ))}
                            </div>
                        )}
                        {candidate.setConflictWith && (
                            <p className="text-xs text-amber-600 dark:text-amber-400"
                               data-testid="builder-compat-conflict">
                                Возможен конфликт с «{candidate.setConflictWith}»
                            </p>
                        )}
                        {warnings.length > 0 && (
                            <p className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400"
                               data-testid="builder-compat-warning">
                                <AlertTriangleIcon className="w-3 h-3 mt-0.5 flex-shrink-0"/>
                                {warnings.map(w => w.message).join('; ')}
                            </p>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-1.5" data-testid="builder-compat-panel">
            <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                Подходит к комплекту
                <span className="text-gray-400 dark:text-gray-500"> · из {pluralItemsOf(setSize)}</span>
                {stepLabel && <span className="text-gray-400 dark:text-gray-500"> · {stepLabel.toLowerCase()}</span>}
            </p>
            {renderBody()}
        </div>
    );
};

export default BuilderCompatPanel;
